import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';

import {
  formatRingCentralNumber,
  useDisconnectRingCentral,
  useRingCentralConnection,
} from './useRingCentral';

type Props = {
  sx?: SxProps<Theme>;
  // Offers Disconnect beside the number - where the connection is managed rather than used.
  showDisconnect?: boolean;
};

/**
 * Under a text editor: the number the text will come from - the sender's own RingCentral line. An
 * environment with no RingCentral app says so here instead, which is why its Send Text is off.
 * Renders nothing while the sender is not connected; RingCentralConnectAlert asks them to.
 */
export default function RingCentralFromNumber({ sx, showDisconnect = false }: Props) {
  const { connection, isLoading, connected } = useRingCentralConnection();
  const { disconnect, disconnecting } = useDisconnectRingCentral();

  if (isLoading || !connection) return null;

  if (!connection.isSetUp) {
    return (
      <Typography variant="caption" color="text.secondary" sx={sx} component="div">
        Text messaging is not set up in this environment.
      </Typography>
    );
  }

  if (!connected) return null;

  return (
    <Typography variant="caption" color="text.secondary" sx={sx} component="div">
      Texts are sent from your RingCentral line ({formatRingCentralNumber(connection.fromNumber)})
      {showDisconnect && (
        <>
          {' '}
          <Link
            component="button"
            // Inside a form a bare <button> defaults to type="submit".
            type="button"
            underline="always"
            disabled={disconnecting}
            onClick={disconnect}
            // A <button> is vertical-align: middle with its own font metrics; inherit both so the
            // link sits on the sentence's baseline.
            sx={{ verticalAlign: 'baseline', font: 'inherit', letterSpacing: 'inherit' }}
          >
            Disconnect
          </Link>
        </>
      )}
    </Typography>
  );
}
